/**
 * The show currency: wallets hold USD (or SKY), and the player picks what amounts are shown in.
 * The store keeps the server's quote on the balance; `store/index.ts` copies it here so plain
 * helpers such as `formatBalance` can read it without a hook. Nothing here imports the store.
 */

/** The unit every non-SKY wallet holds; only amounts in it are converted. */
export const WALLET_UNIT = "USD";

export interface ShowQuote {
  currency: string;
  /** One unit of USD in the show currency. */
  rate: number;
}

let quote: ShowQuote | null = null;

export const setShowQuote = (next: ShowQuote | null | undefined): void => {
  quote = next && next.rate > 0 && next.currency !== WALLET_UNIT ? next : null;
};

export const getShowQuote = (): ShowQuote | null => quote;

const round2 = (value: number) => Math.round(value * 100) / 100;

/** A wallet amount as the player sees it. Amounts in any other unit (SKY) pass through. */
export const toShown = (amount: number, currency: string = WALLET_UNIT) =>
  quote && currency === WALLET_UNIT
    ? { amount: round2(amount * quote.rate), currency: quote.currency }
    : { amount, currency };

/**
 * A typed show amount as what the server books: the smallest USD cent that shows as exactly what
 * was typed, so typing the shown minimum is the minimum and typing the shown balance never asks
 * for more. Rounded down when no cent shows as that figure.
 */
export const toUnit = (amount: number): number => {
  if (!quote) return amount;
  const target = round2(amount);
  const floor = Math.floor((amount / quote.rate) * 100 + 1e-6) / 100;
  for (const step of [0, 1, 2]) {
    const usd = round2(floor + step / 100);
    if (round2(usd * quote.rate) === target) return usd;
  }
  return floor;
};
